import { useEffect, useRef, useState } from 'react';

import {
  BillingApiError,
  abandonCheckoutIntent,
  type StartCheckoutInput,
  fetchPricingCatalog,
  rememberPendingCheckoutSessionRef,
  startCheckout,
  type CatalogEntry,
} from '../../api/billing';
import { SUPPORT_EMAIL } from '../../lib/support';
import { clearPendingOffer, fetchOfferPreview, getPendingOffer } from '../../lib/pendingOffer';

/**
 * Welcome's product step (F-19, F-40): choose a product and pay BEFORE any API key or other setup.
 *
 * The list is the server's catalog (only rows it calls `sellable`); a goal-based recommendation from the
 * questionnaire is highlighted, and a plan chosen before sign-up (`/signup?offer=…`) is preselected.
 * Checkout itself is the existing `POST /v1/billing/checkout`; Stripe returns the customer to the next
 * Welcome step. "Decide later" keeps the free features and moves on; Billing offers plans any time.
 */

export interface ProductOption {
  id: string;
  name: string;
  note: string;
  /** Monthly price in minor units, when the server published one. */
  priceMinor: number | null;
}

export const CHECKOUT_SUCCESS_PATH = '/welcome?step=storage&checkout=success';
export const CHECKOUT_CANCEL_PATH = '/welcome?step=plan&checkout=cancelled';

export function formatMonthlyPrice(minor: number | null): string {
  if (minor === null || !Number.isFinite(minor)) return 'Price shown at checkout';
  return `$${(minor / 100).toFixed(2)} / month`;
}

async function priceFor(entry: CatalogEntry): Promise<number | null> {
  if (typeof entry.pricing?.amount_minor === 'number') return entry.pricing.amount_minor;
  try {
    return (await fetchOfferPreview(entry.id)).price;
  } catch {
    return null;
  }
}

export async function loadProductOptions(): Promise<ProductOption[]> {
  const catalog = await fetchPricingCatalog();
  const sellable = catalog.filter((entry) => entry.sellable);
  return Promise.all(sellable.map(async (entry) => ({
    id: entry.id, name: entry.name, note: entry.note || '', priceMinor: await priceFor(entry),
  })));
}

export function ChooseProductStep({
  recommended, alreadySubscribed, switchRequested = false, onDecideLater, onCheckoutStarted, load = loadProductOptions,
}: {
  recommended: string[];
  alreadySubscribed: boolean;
  /** Subscribed, and a different plan was picked before sign-up. */
  switchRequested?: boolean;
  onDecideLater: () => void;
  /** Called just before the browser leaves for checkout. */
  onCheckoutStarted?: () => void;
  load?: () => Promise<ProductOption[]>;
}) {
  const [options, setOptions] = useState<ProductOption[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [stuckIntent, setStuckIntent] = useState<StartCheckoutInput | null>(null);
  // Leaving this step while the checkout request is in flight cancels the redirect.
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);

  useEffect(() => {
    let active = true;
    load()
      .then((loaded) => {
        if (!active) return;
        const ordered = [...loaded].sort((a, b) =>
          Number(recommended.includes(b.id)) - Number(recommended.includes(a.id)));
        setOptions(ordered);
        const pending = getPendingOffer()?.id;
        const preferred = ordered.find((option) => option.id === pending)
          || ordered.find((option) => recommended.includes(option.id));
        setSelected(preferred?.id || '');
      })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
    // The recommendation is fixed for the life of this step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  async function checkout() {
    if (!selected || busy) return;
    setBusy(true);
    setError('');
    setStuckIntent(null);
    const input = { offerId: selected, successPath: CHECKOUT_SUCCESS_PATH, cancelPath: CHECKOUT_CANCEL_PATH };
    try {
      const handoff = await startCheckout(input);
      if (!live.current) return;
      rememberPendingCheckoutSessionRef(handoff.session_ref);
      clearPendingOffer();
      onCheckoutStarted?.();
      window.location.assign(handoff.redirect_url);
    } catch (failure) {
      if (!live.current) return;
      if (failure instanceof BillingApiError && failure.status === 409) setStuckIntent(input);
      setBusy(false);
      setError(`Checkout couldn’t start, and nothing was charged. Please try again in a moment, or write to ${SUPPORT_EMAIL}.`);
    }
  }

  if (alreadySubscribed) {
    // A second subscription is never started from here: switching plans goes through Billing.
    return (
      <>
        <p role="status">{switchRequested
          ? 'You already have an Auteur plan, and you picked a different one. To switch, change your plan under Billing.'
          : 'You already have an Auteur plan. You can change it any time under Billing.'}</p>
        <div className="onboarding-actions">
          {switchRequested ? <a className="button-link" href="/billing">Change my plan in Billing</a> : null}
          <button type="button" onClick={onDecideLater}>{switchRequested ? 'Keep my current plan and continue' : 'Continue to setup'}</button>
        </div>
      </>
    );
  }

  return (
    <>
      <p>Pick the product you want first; setup comes after. Your plan includes Auteur’s own service. AI
        providers you connect later are billed by them, directly to you.</p>
      {options === null && !failed ? <p role="status">Loading plans…</p> : null}
      {failed || (options && options.length === 0) ? (
        <p role="status">No plan can be bought here right now. You can start with the free features and choose a
          plan later under Billing.</p>
      ) : null}
      {options && options.length ? (
        <fieldset className="welcome-products">
          <legend>Choose a product</legend>
          {options.map((option) => (
            <label key={option.id} className={'welcome-product' + (selected === option.id ? ' selected' : '')}>
              <input type="radio" name="welcome-product" value={option.id} checked={selected === option.id}
                disabled={busy} onChange={() => setSelected(option.id)} />
              <span className="welcome-product-name">{option.name}</span>
              {recommended.includes(option.id) ? <span className="welcome-product-badge"> Recommended for you</span> : null}
              <span className="welcome-product-price"> · {formatMonthlyPrice(option.priceMinor)}</span>
              {option.note ? <span className="welcome-product-note">{option.note}</span> : null}
            </label>
          ))}
        </fieldset>
      ) : null}
      {error ? <p role="alert">{error}</p> : null}
      {stuckIntent ? <div><p>A previous checkout may still exist. Check your billing before starting another purchase.</p>
        <button type="button" onClick={() => { abandonCheckoutIntent(stuckIntent); setStuckIntent(null); setError(''); }}>Forget previous checkout and start over</button></div> : null}
      {options && options.length && !selected ? (
        <p className="settings-muted" id="choose-product-why">Choose a product above to continue to checkout, or decide later.</p>
      ) : null}
      <div className="onboarding-actions">
        {options && options.length ? (
          <button type="button" disabled={!selected || busy}
            aria-describedby={!selected ? 'choose-product-why' : undefined} onClick={() => void checkout()}>
            {busy ? 'Starting checkout…' : 'Continue to secure checkout'}
          </button>
        ) : null}
        <button type="button" className="secondary" disabled={busy} onClick={onDecideLater}>
          {options && options.length ? 'Decide later and use the free features' : 'Continue with the free features'}
        </button>
      </div>
    </>
  );
}
